'use client';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import moment from 'moment';
import { Modal } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdDeleteForever } from 'react-icons/md';
import axios from 'axios';
import { config } from '../../Constants';
import toast from 'react-hot-toast';
import { SpinnerPurple } from '../spinner/Spinner';
import secureLocalStorage from 'react-secure-storage';

export const MessagesTable = ({ data }) => {
  const [opened, { open, close }] = useDisclosure(false);
  const [currentMsg, setCurrentMsg] = useState({});
  const baseURL = config.baseURL;
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { token } = JSON.parse(secureLocalStorage.getItem(`user-info`));

  const deleteMessage = async () => {
    try {
      setLoading(true);
      const { data } = await axios.delete(
        `${baseURL}/contact-us/${currentMsg._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setLoading(false);
      toast.success(data.msg);
      close();
      navigate(0);
    } catch (error) {
      setLoading(false);
      toast.success(error.response.data.msg);
    }
  };

  return (
    <>
      <TableContainer component={Paper} className='messages-table'>
        <Table sx={{ minWidth: 650 }} aria-label='simple table'>
          <TableHead>
            <TableRow>
              <TableCell>NAME</TableCell>
              <TableCell align='right'>EMAIL</TableCell>
              <TableCell align='right'>SENT</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {data.map((info) => {
              const { createdAt, email, name, _id: id } = info;

              return (
                <TableRow
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  style={{ cursor: `pointer` }}
                  key={id}
                  className='user-row'
                  onClick={() => {
                    setCurrentMsg(data.filter((msg) => msg._id === id)[0]);
                    open();
                  }}
                >
                  <TableCell component='th' scope='row' className='name'>
                    {name}
                  </TableCell>
                  <TableCell align='right'>{email}</TableCell>
                  <TableCell align='right'>
                    {moment(createdAt).format(`LLL`)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Modal
        opened={opened}
        onClose={close}
        centered
        size='lg'
        className='message-modal'
        overlayProps={{
          backgroundOpacity: 0.55,
        }}
      >
        <div className='title'>
          <h2>
            Message sent by <span>{currentMsg.name}</span>
          </h2>
          <h3>{currentMsg.email}</h3>
        </div>

        <div className='info'>
          <p>{currentMsg.message}</p>
        </div>

        <div className='del-btn'>
          {loading ? (
            <SpinnerPurple />
          ) : (
            <MdDeleteForever onClick={deleteMessage} />
          )}
        </div>
      </Modal>
    </>
  );
};

export const UsersTable = ({ data }) => {
  const navigate = useNavigate();

  return (
    <TableContainer component={Paper} className='users-table'>
      <Table sx={{ minWidth: 650 }} aria-label='simple table'>
        <TableHead>
          <TableRow>
            <TableCell>NAME</TableCell>
            <TableCell align='right'>EMAIL</TableCell>
            <TableCell align='right'>PHONE</TableCell>
            <TableCell align='right'>SIGNED UP</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((user) => {
            const { _id, createdAt, email, fullname, phone } = user;
            return (
              <TableRow
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                style={{ cursor: `pointer` }}
                key={_id}
                className='user-row'
                onClick={() => navigate(`/dashboard/users/${_id}`)}
              >
                <TableCell component='th' scope='row'>
                  {fullname}
                </TableCell>
                <TableCell align='right'>{email}</TableCell>
                <TableCell align='right'>{phone}</TableCell>
                <TableCell align='right'>
                  {moment(createdAt).format(`LLL`)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const DepositsTable = ({ data }) => {
  const navigate = useNavigate();

  return (
    <TableContainer component={Paper} className='deposits-table'>
      <Table sx={{ minWidth: 650 }} aria-label='simple table'>
        <TableHead>
          <TableRow>
            <TableCell>NAME</TableCell>
            <TableCell align='right'>DEPOSIT AMOUNT</TableCell>
            <TableCell align='right'>TIME</TableCell>
            <TableCell align='right'>STATUS</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((deposit) => {
            const { _id, createdAt, userName, status, amount } = deposit;
            return (
              <TableRow
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                style={{ cursor: `pointer` }}
                key={_id}
                className='deposit-row'
                onClick={() => navigate(`/dashboard/deposits/${_id}`)}
              >
                <TableCell component='th' scope='row' className='name'>
                  {userName}
                </TableCell>
                <TableCell align='right'>${amount}</TableCell>
                <TableCell align='right'>
                  {moment(createdAt).format(`LLL`)}
                </TableCell>
                <TableCell align='right' className='status'>
                  <span className={status}>{status}</span>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const WithdrawalsTable = ({ data }) => {
  const navigate = useNavigate();

  return (
    <TableContainer component={Paper} className='withdrawal-table'>
      <Table sx={{ minWidth: 650 }} aria-label='simple table'>
        <TableHead>
          <TableRow>
            <TableCell>NAME</TableCell>
            <TableCell align='right'>WITHDRAWAL AMOUNT</TableCell>
            <TableCell align='right'>TIME</TableCell>
            <TableCell align='right'>STATUS</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((withdrawal) => {
            const { _id, createdAt, userName, status, amount } = withdrawal;
            return (
              <TableRow
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                style={{ cursor: `pointer` }}
                key={_id}
                className='withdrawal-row'
                onClick={() => navigate(`/dashboard/withdrawals/${_id}`)}
              >
                <TableCell component='th' scope='row' className='name'>
                  {userName}
                </TableCell>
                <TableCell align='right'>${amount}</TableCell>
                <TableCell align='right'>
                  {moment(createdAt).format(`LLL`)}
                </TableCell>
                <TableCell align='right' className='status'>
                  <span className={status}>{status}</span>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const KYCsTable = ({ data }) => {
  const navigate = useNavigate();

  return (
    <TableContainer component={Paper} className='kycs-table'>
      <Table sx={{ minWidth: 650 }} aria-label='simple table'>
        <TableHead>
          <TableRow>
            <TableCell>NAME</TableCell>
            <TableCell align='right'>DOCUMENT NAME</TableCell>
            <TableCell align='right'>TIME SENT</TableCell>
            <TableCell align='right'>STATUS</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((kyc) => {
            const { _id, createdAt, userName, status, document } = kyc;
            return (
              <TableRow
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                style={{ cursor: `pointer` }}
                key={_id}
                className='kyc-row'
                onClick={() => navigate(`/dashboard/kyc/${_id}`)}
              >
                <TableCell component='th' scope='row' className='name'>
                  {userName}
                </TableCell>
                <TableCell align='right' className='document'>
                  {document}
                </TableCell>
                <TableCell align='right'>
                  {moment(createdAt).format(`LLL`)}
                </TableCell>
                <TableCell align='right' className='status'>
                  <span className={status}>{status}</span>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const InvestmentsTable = ({ data }) => {
  const navigate = useNavigate();

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <TableContainer component={Paper} className='investments-table'>
      <Table sx={{ minWidth: 650 }} aria-label='simple table'>
        <TableHead>
          <TableRow>
            <TableCell>USER</TableCell>
            <TableCell align='right'>PLAN</TableCell>
            <TableCell align='right'>AMOUNT</TableCell>
            <TableCell align='right'>PROFIT</TableCell>
            <TableCell align='right'>END DATE</TableCell>
            <TableCell align='right'>STATUS</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((investment) => {
            const { _id, userName, planTitle, amount, profitAccrued, endDate, status } = investment;
            return (
              <TableRow
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                style={{ cursor: `pointer` }}
                key={_id}
                className='investment-row'
                onClick={() => navigate(`/dashboard/investments/${_id}`)}
              >
                <TableCell component='th' scope='row' className='name'>
                  {userName}
                </TableCell>
                <TableCell align='right'>{planTitle}</TableCell>
                <TableCell align='right'>${amount.toLocaleString()}</TableCell>
                <TableCell align='right' className='profit'>
                  ${profitAccrued.toFixed(2)}
                </TableCell>
                <TableCell align='right'>{formatDate(endDate)}</TableCell>
                <TableCell align='right' className='status'>
                  <span className={status}>{status}</span>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const TradingPlansTable = ({ data }) => {
  const navigate = useNavigate();
  const baseURL = config.baseURL;
  const { token } = JSON.parse(secureLocalStorage.getItem(`user-info`));
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const [toggleModalOpened, { open: openToggleModal, close: closeToggleModal }] = useDisclosure(false);

  const formatAmount = (plan) => {
    if (plan.maxAmount) {
      return `$${plan.minAmount.toLocaleString()} - $${plan.maxAmount.toLocaleString()}`;
    }
    return `$${plan.minAmount.toLocaleString()}`;
  };

  const openDeleteConfirm = (e, plan) => {
    e.stopPropagation();
    setSelectedPlan(plan);
    openDeleteModal();
  };

  const openToggleConfirm = (e, plan) => {
    e.stopPropagation();
    setSelectedPlan(plan);
    openToggleModal();
  };

  const handleDelete = async () => {
    if (!selectedPlan) return;

    setLoading(true);
    try {
      await axios.delete(`${baseURL}/trading-plans/admin/${selectedPlan._id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success('Trading plan deleted');
      closeDeleteModal();
      navigate(0);
    } catch (error) {
      toast.error(error.response?.data?.msg || 'Failed to delete plan');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async () => {
    if (!selectedPlan) return;

    setLoading(true);
    try {
      await axios.patch(
        `${baseURL}/trading-plans/admin/${selectedPlan._id}`,
        { isActive: !selectedPlan.isActive },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success(`Plan ${selectedPlan.isActive ? 'deactivated' : 'activated'} successfully`);
      closeToggleModal();
      navigate(0);
    } catch (error) {
      toast.error(error.response?.data?.msg || 'Failed to update plan');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <TableContainer component={Paper} className='trading-plans-table'>
        <Table sx={{ minWidth: 650 }} aria-label='simple table'>
          <TableHead>
            <TableRow>
              <TableCell>TITLE</TableCell>
              <TableCell align='right'>AMOUNT</TableCell>
              <TableCell align='right'>DURATION</TableCell>
              <TableCell align='right'>PROFIT</TableCell>
              <TableCell align='right'>STATUS</TableCell>
              <TableCell align='right'>ACTIONS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((plan) => {
              const { _id, title, duration, expectedProfit, isActive } = plan;
              return (
                <TableRow
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  style={{ cursor: `pointer` }}
                  key={_id}
                  className='plan-row'
                  onClick={() => navigate(`/dashboard/trading-plans/${_id}`)}
                >
                  <TableCell component='th' scope='row' className='name'>
                    {title}
                  </TableCell>
                  <TableCell align='right'>{formatAmount(plan)}</TableCell>
                  <TableCell align='right'>{duration} days</TableCell>
                  <TableCell align='right'>{expectedProfit}%</TableCell>
                  <TableCell align='right' className='status'>
                    <span className={isActive ? 'approved' : 'pending'}>
                      {isActive ? 'Active' : 'Inactive'}
                    </span>
                  </TableCell>
                  <TableCell align='right' className='actions'>
                    <button
                      className={`btn-small ${isActive ? 'btn-warning' : 'btn-success'}`}
                      onClick={(e) => openToggleConfirm(e, plan)}
                    >
                      {isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      className='btn-small btn-danger'
                      onClick={(e) => openDeleteConfirm(e, plan)}
                    >
                      Delete
                    </button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        centered
        size='sm'
        title='Delete Trading Plan'
        className='confirm-modal'
        overlayProps={{
          backgroundOpacity: 0.55,
        }}
      >
        <div className='confirm-content'>
          <p>
            Are you sure you want to delete <strong>"{selectedPlan?.title}"</strong>?
          </p>
          <p className='warning-text'>This action cannot be undone.</p>
          <div className='confirm-buttons'>
            <button className='btn-cancel' onClick={closeDeleteModal} disabled={loading}>
              Cancel
            </button>
            <button className='btn-confirm btn-danger' onClick={handleDelete} disabled={loading}>
              {loading ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Toggle Active Confirmation Modal */}
      <Modal
        opened={toggleModalOpened}
        onClose={closeToggleModal}
        centered
        size='sm'
        title={selectedPlan?.isActive ? 'Deactivate Plan' : 'Activate Plan'}
        className='confirm-modal'
        overlayProps={{
          backgroundOpacity: 0.55,
        }}
      >
        <div className='confirm-content'>
          <p>
            Are you sure you want to {selectedPlan?.isActive ? 'deactivate' : 'activate'}{' '}
            <strong>"{selectedPlan?.title}"</strong>?
          </p>
          {selectedPlan?.isActive && (
            <p className='info-text'>Users will no longer see this plan.</p>
          )}
          <div className='confirm-buttons'>
            <button className='btn-cancel' onClick={closeToggleModal} disabled={loading}>
              Cancel
            </button>
            <button
              className={`btn-confirm ${selectedPlan?.isActive ? 'btn-warning' : 'btn-success'}`}
              onClick={handleToggleActive}
              disabled={loading}
            >
              {loading
                ? 'Processing...'
                : selectedPlan?.isActive
                ? 'Deactivate'
                : 'Activate'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};
